import AddActivityDialog from '../MachineActivity/AddDialog';
import { useHistory, useLocation } from 'react-router-dom';
import React, { useState } from 'react';
import { Modal, Button as LegacyButton } from 'antd';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from 'components/ui/dropdown-menu';
import { Activity, Plus, ChevronDown } from 'lucide-react';
import AddForm from './AddForm';
import AddFormAqSensor from './AddFormAqSensor';
import { useIsMobile } from '../../../hooks/useIsMobile';
import AdminOnly from '../../../components/AdminOnly';
import { SensorType } from '../../../general/enums';
import AddFormGsmPlant from './AddFormGsmPlant';
import AddFormGpsSensor from './AddFormGpsSensor';
import AddFormGpsSimTrackingSensor from './AddFormGpsSensorSimTracking';
import { useParams } from 'react-router';
import firebase from 'firebase/compat/app';
import Message, { Type } from 'components/Notification/Message';
import AddFormLevelSensor from './AddFormLevelSensor';
import AddFormPlantModule from './AddFormPlantModule';

async function setupGPSDemoSensor(orgId: string) {
  try {
    if (!orgId) throw Error('setupGPSDemoSensor:No orgId');
    await firebase
      .firestore()
      .collection('orgs')
      .doc(orgId)
      .collection('sensors')
      .add({
        createTime: new Date(),
        identifier: 'DF_HIGH_GPS_DEMO',
        location: new firebase.firestore.GeoPoint(55.63755, 12.25355),
        sensorBoundaries: {
          bottomLeftBoundary: new firebase.firestore.GeoPoint(55.636196, 12.251927),
          bottomRightBoundary: new firebase.firestore.GeoPoint(55.636158, 12.255082),
          topLeftBoundary: new firebase.firestore.GeoPoint(55.638736, 12.251987),
          topRightBoundary: new firebase.firestore.GeoPoint(55.638681, 12.255184),
        },
        title: 'GPS - DEMO 1',
        type: SensorType.GPS_GY_NEO,
      });
    Message({
      key: 'addSensorDemo',
      message: `Alt blev tilføjet korrekt`,
    });
  } catch (error) {
    Message({
      key: 'addSensorDemo',
      type: Type.ERROR,
      message: `Der skete en fejl`,
      description: `Kunne ikke oprette demo sensor, prøv venligst igen`,
    });
    console.error(error);
  }
}
async function setupDustDemoSensor(orgId: string) {
  try {
    if (!orgId) throw Error('setupDustDemoSensor:No orgId');
    await firebase
      .firestore()
      .collection('orgs')
      .doc(orgId)
      .collection('sensors')
      .add({
        createTime: new Date(),
        deviceId: 'HIGH_DUST_DEMO',
        orgId: orgId,
        thresholds: {
          co2: 1300,
          decibel: 60,
          humidity: 60,
          lux: 1000,
          pm_10: 350,
          pm_2_5: 250,
          temperature: 30,
        },
        title: 'AQ Demo',
        type: SensorType.ESP_AQ,
      });
    Message({
      key: 'addSensorDemo',
      message: `Alt blev tilføjet korrekt`,
    });
  } catch (error) {
    Message({
      key: 'addSensorDemo',
      type: Type.ERROR,
      message: `Der skete en fejl`,
      description: `Kunne ikke oprette demo sensor, prøv venligst igen`,
    });
    console.error(error);
  }
}

function AddCard() {
  const { orgId } = useParams<{ orgId: string }>();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const requestedType = params.get('setupType') as SensorType;
  const allowedSetupTypes = [
    SensorType.MACHINE_ACTIVITY,
    SensorType.ESP_AQ,
    SensorType.FOOBOT,
    SensorType.GSM_PLANT,
    SensorType.GPS_GY_NEO,
    SensorType.GPS_SIMTRACKING,
    SensorType.LEVEL_SENSOR_INTERNAL,
    SensorType.PLANT_MODULE,
  ];
  const [type, settype] = useState<SensorType | null>(() =>
    allowedSetupTypes.includes(requestedType) ? requestedType : null
  );
  const prefill = { deviceId: params.get('deviceId') || '', title: params.get('title') || '' };
  const isMobile = useIsMobile();
  const showModal = (type: SensorType) => settype(type);
  const handleCancel = () => {
    settype(null);
    if (params.has('setupType')) {
      ['setupType', 'deviceId', 'title'].forEach((key) => params.delete(key));
      history.replace({ ...location, search: params.toString() });
    }
  };

  return (
    <AdminOnly>
      {type === SensorType.MACHINE_ACTIVITY && (
        <AddActivityDialog onClose={handleCancel} prefill={prefill} />
      )}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button>
            <Plus aria-hidden="true" />
            Tilføj sensor
            <ChevronDown aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-[70vh] w-64 overflow-y-auto">
          <DropdownMenuItem onSelect={() => showModal(SensorType.MACHINE_ACTIVITY)}>
            <Activity />
            Maskinaktivitet
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Luftkvalitet</DropdownMenuLabel>
          <DropdownMenuItem onSelect={() => showModal(SensorType.ESP_AQ)}>
            AQ-sensor
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => showModal(SensorType.FOOBOT)}>Foobot</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Anlæg og niveau</DropdownMenuLabel>
          <DropdownMenuItem onSelect={() => showModal(SensorType.GSM_PLANT)}>
            GSM-anlæg
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => showModal(SensorType.GPS_SIMTRACKING)}>
            GPS-anlæg (mobilt)
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => showModal(SensorType.LEVEL_SENSOR_INTERNAL)}>
            Niveaumåler
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => showModal(SensorType.PLANT_MODULE)}>
            Anlægsmodul
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>GPS</DropdownMenuLabel>
          <DropdownMenuItem onSelect={() => showModal(SensorType.GPS_SIMTRACKING)}>
            GPS-sensor (ny)
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => showModal(SensorType.GPS_GY_NEO)}>
            GPS-sensor
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Demo</DropdownMenuLabel>
          <DropdownMenuItem onSelect={() => setupGPSDemoSensor(orgId)}>GPS-demo</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setupDustDemoSensor(orgId)}>AQ-demo</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Modal
        key={SensorType.ESP_AQ}
        title="Tilføj sensor"
        open={type === SensorType.ESP_AQ}
        // onOk={e => handleOk(e, email)}
        onCancel={handleCancel}
        width={isMobile ? '100%' : '50%'}
        centered
        footer={[
          <LegacyButton key="back" onClick={handleCancel}>
            Cancel
          </LegacyButton>,
        ]}
      >
        <AddFormAqSensor onClose={handleCancel} prefill={prefill} />
      </Modal>
      <Modal
        key={SensorType.FOOBOT}
        title="Tilføj sensor"
        open={type === SensorType.FOOBOT}
        // onOk={e => handleOk(e, email)}
        onCancel={handleCancel}
        width={isMobile ? '100%' : '50%'}
        centered
        footer={[
          <LegacyButton key="back" onClick={handleCancel}>
            Cancel
          </LegacyButton>,
        ]}
      >
        <AddForm onClose={handleCancel} prefill={prefill} />
      </Modal>
      <Modal
        key={SensorType.GSM_PLANT}
        title="Tilføj sensor"
        open={type === SensorType.GSM_PLANT}
        // onOk={e => handleOk(e, email)}
        onCancel={handleCancel}
        width={isMobile ? '100%' : '50%'}
        centered
        footer={[
          <LegacyButton key="back" onClick={handleCancel}>
            Cancel
          </LegacyButton>,
        ]}
      >
        <AddFormGsmPlant onClose={handleCancel} prefill={prefill} />
      </Modal>
      <Modal
        key={SensorType.GPS_GY_NEO}
        title="Tilføj sensor"
        open={type === SensorType.GPS_GY_NEO}
        // onOk={e => handleOk(e, email)}
        onCancel={handleCancel}
        width={isMobile ? '100%' : '50%'}
        centered
        footer={[
          <LegacyButton key="back" onClick={handleCancel}>
            Cancel
          </LegacyButton>,
        ]}
      >
        <AddFormGpsSensor onClose={handleCancel} prefill={prefill} />
      </Modal>
      <Modal
        key={SensorType.GPS_SIMTRACKING}
        title="Tilføj sensor"
        open={type === SensorType.GPS_SIMTRACKING}
        // onOk={e => handleOk(e, email)}
        onCancel={handleCancel}
        width={isMobile ? '100%' : '50%'}
        centered
        footer={[
          <LegacyButton key="back" onClick={handleCancel}>
            Cancel
          </LegacyButton>,
        ]}
      >
        <AddFormGpsSimTrackingSensor onClose={handleCancel} prefill={prefill} />
      </Modal>
      <Modal
        key={SensorType.LEVEL_SENSOR_INTERNAL}
        title="Tilføj sensor"
        open={type === SensorType.LEVEL_SENSOR_INTERNAL}
        // onOk={e => handleOk(e, email)}
        onCancel={handleCancel}
        width={isMobile ? '100%' : '50%'}
        centered
        footer={[
          <LegacyButton key="back" onClick={handleCancel}>
            Cancel
          </LegacyButton>,
        ]}
      >
        <AddFormLevelSensor onClose={handleCancel} prefill={prefill} />
      </Modal>
      <Modal
        key={SensorType.PLANT_MODULE}
        title="Tilføj sensor"
        open={type === SensorType.PLANT_MODULE}
        onCancel={handleCancel}
        width={isMobile ? '100%' : '50%'}
        centered
        footer={[
          <LegacyButton key="back" onClick={handleCancel}>
            Cancel
          </LegacyButton>,
        ]}
      >
        <AddFormPlantModule onClose={handleCancel} prefill={prefill} />
      </Modal>
    </AdminOnly>
  );
}

export default AddCard;
